import React, { useEffect, useMemo, useState } from "react";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";

interface Skill {
  id: number;
  skill: string;
}

interface SelectSkillFormProps {
  onSuccess: () => void;
  onError?: () => void;
  existingSkills?: Skill[];
}

const SelectSkillForm = ({
  onSuccess,
  existingSkills = [],
  onError,
}: SelectSkillFormProps) => {
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);

  const { addToast } = useToaster();

  // FETCH SKILLS
  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      setLoading(true);

      const response = await apiService.get("/api/admin/skill");

      console.log("FETCHED SKILLS => ", response);

      const skills = Array.isArray(response?.data)
        ? response.data
        : [];
      if (existingSkills.length > 0) {
        console.log("EXISTING SKILLS => ", existingSkills);
        const existingIds = existingSkills.map((skill) => skill.id);
        console.log("EXISTING SKILL IDS => ", existingIds);
        const filteredSkills = skills.filter(
          (skill) => !existingIds.includes(skill.id)
        );
        console.log("FILTERED SKILLS => ", filteredSkills);
        setAllSkills(filteredSkills);
      } else {
            setAllSkills(skills);
      }
    } catch (error) {
      console.error("Failed to fetch skills", error);
      setAllSkills([]);
    } finally {
      setLoading(false);
    }
  };

  // FILTER AVAILABLE SKILLS
  // HIDE:
  // 1. existing skills
  // 2. newly selected skills
  const availableSkills = useMemo(() => {
    return allSkills.filter((skill) => {
      const alreadyExisting = existingSkills.some(
        (item) => item.id === skill.id
      );

      const alreadySelected = selectedSkills.some(
        (item) => item.id === skill.id
      );

      return !alreadyExisting && !alreadySelected;
    });
  }, [allSkills, existingSkills, selectedSkills]);

  // SELECT NEW SKILL
  const handleSelectSkill = (skill: Skill) => {
    setSelectedSkills((prev) => [...prev, skill]);
  };

  // REMOVE ONLY NEWLY ADDED SKILL
  const handleRemoveSkill = (skill: Skill) => {
    setSelectedSkills((prev) =>
      prev.filter((item) => item.id !== skill.id)
    );
  };

  // SUBMIT ONLY NEW SKILLS
  const handleSubmit = async () => {
    try {
      const payload = selectedSkills.map((skill) => ({
        skill: skill.skill,
      }));

      console.log("SUBMIT PAYLOAD => ", payload);

      await apiService.post("/api/user/skill/add", payload);

      addToast("Skills added successfully", "success");

      onSuccess();
    } catch (error) {
      console.error("Submit failed", error);
    const message = error?.response?.data || "Failed to add skills. Please try again.";
      addToast(
       message,
        "error"
      );
      if (onError) {
        onError();
      }
    }
  };

  return (
    <div className="w-full max-w-2xl p-6 mx-auto bg-white dark:bg-gray-800 rounded-xl shadow">
      {/* <h2 className="mb-5 text-2xl font-bold text-gray-900 dark:text-white">
        Select Your Skills
      </h2> */}

      {/* SELECTED SKILLS */}
      <div className="mb-6">
        <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
          Selected Skills
        </h3>

        <div className="flex flex-wrap gap-2 min-h-[70px] p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">

          {/* EXISTING SKILLS - READONLY */}
          {existingSkills.map((skill) => (
            <div
              key={`existing-${skill.id}`}
              className="flex items-center gap-2 px-3 py-1 text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg cursor-not-allowed"
              title="Already added"
            >
              <span>{skill.skill}</span>
            </div>
          ))}

          {/* NEWLY SELECTED SKILLS */}
          {selectedSkills.map((skill) => (
            <div
              key={`selected-${skill.id}`}
              className="flex items-center gap-2 px-3 py-1 text-white bg-blue-500 rounded-lg"
            >
              <span>{skill.skill}</span>

              <button
                onClick={() => handleRemoveSkill(skill)}
                className="text-sm font-bold hover:text-red-200"
              >
                ✕
              </button>
            </div>
          ))}

          {/* EMPTY STATE */}
          {existingSkills.length === 0 &&
            selectedSkills.length === 0 && (
              <p className="text-gray-400 dark:text-gray-500">
                No skills selected
              </p>
            )}
        </div>
      </div>

      {/* AVAILABLE SKILLS */}
      <div>
        <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
          Available Skills
        </h3>

        <div className="flex flex-wrap gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg min-h-[70px] bg-white dark:bg-gray-900">
          {loading ? (
            <p className="text-gray-500 dark:text-gray-400">Loading skills...</p>
          ) : availableSkills.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500">
              No skills available
            </p>
          ) : (
            availableSkills.map((skill) => (
              <button
                key={skill.id}
                onClick={() => handleSelectSkill(skill)}
                className="flex items-center gap-2 px-3 py-1 transition border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {skill.skill}
              </button>
            ))
          )}
        </div>
      </div>

      {/* SUBMIT BUTTON */}
      <div className="mt-6">
        <button
          onClick={handleSubmit}
          disabled={selectedSkills.length === 0}
          className={`px-5 py-2 text-white rounded-lg transition
            ${
              selectedSkills.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default SelectSkillForm;